/**
 * Marketer View sidebar (SYR-68).
 *
 * Flat 6-item primary nav ("The …") + a collapsible Admin group and Help in the
 * footer. Primary items navigate within the brand root and clear the deep-link
 * return point; Admin/Help link out to the existing dashboard pages. Custom
 * (not Antd Menu) so the active item gets the design's amber-soft pill.
 */

import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { DownOutlined, RightOutlined, SettingOutlined } from '@ant-design/icons';
import { useAuth } from '../../../hooks/useAuth';
import { useGetBrandModelBeliefsAccessQuery } from '../../../services/nestjs-base-api';
import { useMarketerScope } from '../scope/MarketerScopeContext';
import { useMarketerRoot } from '../../../hooks/useDashboardConfig';
import { Utils } from '../../../utils/utils';
import { useNavReturn } from '../nav/NavReturnContext';
import { PRIMARY_NAV, ADMIN_NAV, HELP_NAV } from './navItems';
import { THEME } from '../config/theme';

interface MarketerSidebarProps {
  brandHash: string;
  collapsed: boolean;
  onNavigate?: () => void;
}

const LogoIcon: React.FunctionComponent<{ size?: number }> = ({ size = 36 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
    <circle cx="24" cy="24" r="20" stroke="#3B82F6" strokeWidth="2" opacity="0.3" />
    <circle cx="24" cy="24" r="10" fill="#F59E0B" />
    <circle cx="24" cy="4" r="4" fill="#3B82F6" />
    <circle cx="6" cy="34" r="4" fill="#22C55E" />
    <circle cx="42" cy="34" r="4" fill="#3B82F6" />
  </svg>
);

export const MarketerSidebar: React.FunctionComponent<MarketerSidebarProps> = ({
  brandHash,
  collapsed,
  onNavigate,
}) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { clearReturn } = useNavReturn();
  const [adminOpen, setAdminOpen] = useState(true);

  const marketerRoot = useMarketerRoot();
  const root = `${marketerRoot}/${brandHash}`;
  const showCollapsed = collapsed;

  // The Machines is hidden unless the feature is actually available to this
  // session. `enabled` carries the platform-admin / impersonation bypass, so a
  // dark feature stays visible to whoever is testing it on a real brand and
  // absent for the customer — rather than offering a destination that can only
  // say "not live yet". The page keeps its own gate states for a direct URL.
  const { activeBrand } = useMarketerScope();
  const { data: modelBeliefsAccess } = useGetBrandModelBeliefsAccessQuery(
    {
      jwtToken: user?.jwtToken ?? '',
      organisationId: user?.organisation?.id ?? 0,
      brandId: activeBrand?.id ?? 0,
    },
    { skip: !user?.jwtToken || !user?.organisation?.id || !activeBrand?.id },
  );
  const showModelBeliefs = !!modelBeliefsAccess?.entitled && modelBeliefsAccess?.enabled;

  const primaryNav = PRIMARY_NAV.filter(
    (item) => item.key !== 'model-beliefs' || showModelBeliefs,
  );

  const isActive = (path: string): boolean => {
    const full = path ? `${root}/${path}` : root;
    if (path === '') {
      // Briefing index: active only on exact root (allow trailing slash).
      return (
        location.pathname === root || location.pathname === `${root}/`
      );
    }
    return location.pathname.startsWith(full);
  };

  const goPrimary = (path: string): void => {
    clearReturn();
    navigate(path ? `${root}/${path}` : root);
    onNavigate?.();
  };

  const adminUser = Utils.userIsOrganisationAdmin(user);
  const adminItems = ADMIN_NAV.filter((i) => !i.adminOnly || adminUser);

  const itemStyle = (active: boolean): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    width: '100%',
    padding: showCollapsed ? '11px 0' : '11px 14px',
    justifyContent: showCollapsed ? 'center' : 'flex-start',
    margin: '2px 0',
    borderRadius: 10,
    border: 'none',
    cursor: 'pointer',
    background: active ? THEME.primarySoft : 'transparent',
    color: active ? THEME.primaryDark : THEME.textBase,
    fontWeight: active ? 600 : 500,
    fontSize: 14,
    textAlign: 'left',
    transition: 'background 0.15s, color 0.15s',
    boxSizing: 'border-box',
  });

  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: '#fff',
      }}
    >
      {/* Logo */}
      <div
        style={{
          padding: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          borderBottom: `1px solid ${THEME.borderSubtle}`,
          marginBottom: 8,
          cursor: 'pointer',
        }}
        onClick={() => goPrimary('')}
      >
        <LogoIcon size={36} />
        {!showCollapsed && (
          <div>
            <div style={{ fontWeight: 700, fontSize: 18, lineHeight: 1.1 }}>
              <span style={{ color: '#1f2937' }}>BrandScience</span>
              <span style={{ color: THEME.textMuted }}>.ai</span>
            </div>
            <div style={{ fontSize: 12, color: THEME.textSecondary }}>
              {intl.formatMessage({
                id: 'layout.dashboard.tagline',
                defaultMessage: 'Brand Analytics Platform',
              })}
            </div>
          </div>
        )}
      </div>

      {/* Primary nav */}
      <nav
        style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          padding: '4px 8px 0',
        }}
      >
        {primaryNav.map((item) => {
          const active = isActive(item.path);
          return (
            <button
              key={item.key}
              type="button"
              onClick={() => goPrimary(item.path)}
              style={itemStyle(active)}
              title={intl.formatMessage({
                id: item.labelId,
                defaultMessage: item.defaultLabel,
              })}
            >
              <span style={{ fontSize: 18, display: 'inline-flex' }}>
                {item.icon}
              </span>
              {!showCollapsed && (
                <span>
                  {intl.formatMessage({
                    id: item.labelId,
                    defaultMessage: item.defaultLabel,
                  })}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {/* Footer: Admin group + Help */}
      <div
        style={{
          borderTop: `1px solid ${THEME.borderSubtle}`,
          padding: '8px',
          overflowX: 'hidden',
        }}
      >
        {!showCollapsed && (
          <button
            type="button"
            onClick={() => setAdminOpen((o) => !o)}
            style={{
              ...itemStyle(false),
              color: THEME.textSecondary,
              fontWeight: 500,
            }}
          >
            <SettingOutlined style={{ fontSize: 18 }} />
            <span style={{ flex: 1 }}>
              {intl.formatMessage({
                id: 'marketerView.nav.admin',
                defaultMessage: 'Admin',
              })}
            </span>
            {adminOpen ? <DownOutlined /> : <RightOutlined />}
          </button>
        )}
        {(adminOpen || showCollapsed) &&
          adminItems.map((item) => {
            const active = isActive(item.path);
            return (
            <button
              key={item.key}
              type="button"
              onClick={() => goPrimary(item.path)}
              style={{
                ...itemStyle(active),
                paddingLeft: showCollapsed ? undefined : 32,
                fontWeight: active ? 600 : 500,
                color: active ? THEME.primaryDark : THEME.textSecondary,
              }}
              title={intl.formatMessage({
                id: item.labelId,
                defaultMessage: item.defaultLabel,
              })}
            >
              <span style={{ fontSize: 16, display: 'inline-flex' }}>
                {item.icon}
              </span>
              {!showCollapsed && (
                <span>
                  {intl.formatMessage({
                    id: item.labelId,
                    defaultMessage: item.defaultLabel,
                  })}
                </span>
              )}
            </button>
            );
          })}
        <button
          type="button"
          onClick={() => goPrimary(HELP_NAV.path)}
          style={{
            ...itemStyle(isActive(HELP_NAV.path)),
            color: isActive(HELP_NAV.path) ? THEME.primaryDark : THEME.textSecondary,
          }}
          title={intl.formatMessage({
            id: HELP_NAV.labelId,
            defaultMessage: HELP_NAV.defaultLabel,
          })}
        >
          <span style={{ fontSize: 16, display: 'inline-flex' }}>
            {HELP_NAV.icon}
          </span>
          {!showCollapsed && (
            <span>
              {intl.formatMessage({
                id: HELP_NAV.labelId,
                defaultMessage: HELP_NAV.defaultLabel,
              })}
            </span>
          )}
        </button>
      </div>
    </div>
  );
};
